import type { RecencyWindow } from "@packages/core/time/recencyWindows.ts";
import type { GoodVibesVisibility } from "../../shared/goodVibesVisibility.ts";
import type { GiveGoodVibeInput } from "./goodVibesComposeTypes.ts";
import type { GoodVibesDirection } from "./goodVibesDirections.ts";

export const RECOGNITION_AREA_PROPERTIES = { Area: "Recognition" } as const;

export const RECOGNITION_LOCATIONS = {
    Org: "Org",
    Team: "Team",
    Home: "Home",
    PeopleGoodVibes: "People - Good Vibes",
    PeopleHeader: "People - Header",
    PeopleTimeline: "People - Timeline",
    PeopleEngagement: "People - Engagement",
    PeopleReviews: "People - Reviews",
    TeamReviews: "Team - Reviews",
    AnalyticsGoodVibes: "Analytics - Good Vibes"
} as const;

export type RecognitionInteractionBy = "Sender" | "Recipient" | "Manager" | "Member";

export type RecognitionReactionTarget = "Card" | "Comment";

export interface RecognitionTrackedCard {
    isPublic?: boolean;
    sender?: { id?: string };
    recipients: ReadonlyArray<{ id: string; managerId?: string }>;
}

export interface GoodVibesFiltersState {
    audience?: GoodVibesVisibility;
    direction?: GoodVibesDirection;
    collections?: ReadonlyArray<string>;
    timeframe?: RecencyWindow;
}

export interface RecognitionCardEditSnapshot {
    message: string;
    isPublic: boolean;
    recipientCount: number;
}

type RecognitionEditedField = "Message" | "Visibility" | "Recipients";

type RecognitionVisibility = "Public" | "Private";

const RECOGNITION_TYPES = {
    Officevibe: "Officevibe Prompt",
    Custom: "Custom Prompt",
    FromScratch: "From Scratch"
} as const satisfies Record<GiveGoodVibeInput["promptSource"], string>;

const MILLISECONDS_PER_HOUR = 3_600_000;

function isSameMember(left: string | undefined, right: string | undefined) {
    return !!left && !!right && left.toLowerCase() === right.toLowerCase();
}

function toVisibility(isPublic: boolean): RecognitionVisibility {
    return isPublic ? "Public" : "Private";
}

export function getRecognitionInteractionBy(card: RecognitionTrackedCard, memberId: string | undefined): RecognitionInteractionBy {
    if (isSameMember(card.sender?.id, memberId)) {
        return "Sender";
    }
    if (card.recipients.some(recipient => isSameMember(recipient.id, memberId))) {
        return "Recipient";
    }
    if (card.recipients.some(recipient => isSameMember(recipient.managerId, memberId))) {
        return "Manager";
    }

    return "Member";
}

export function getRecognitionDeletedBy(card: RecognitionTrackedCard, memberId: string | undefined): Exclude<RecognitionInteractionBy, "Member"> {
    const interactionBy = getRecognitionInteractionBy(card, memberId);

    return interactionBy === "Member" ? "Manager" : interactionBy;
}

export function toRecognitionType(promptSource: GiveGoodVibeInput["promptSource"]) {
    return RECOGNITION_TYPES[promptSource];
}

export function buildRecognitionCardEditProperties(before: RecognitionCardEditSnapshot, after: RecognitionCardEditSnapshot, createdAt: string | undefined, now: number): Record<string, unknown> | undefined {
    const fieldsEdited: RecognitionEditedField[] = [];
    const properties: Record<string, unknown> = {};

    if (before.message !== after.message) {
        fieldsEdited.push("Message");
        properties["Message Length Before"] = before.message.length;
        properties["Message Length After"] = after.message.length;
    }
    if (before.isPublic !== after.isPublic) {
        fieldsEdited.push("Visibility");
        properties["Visibility Before"] = toVisibility(before.isPublic);
        properties["Visibility After"] = toVisibility(after.isPublic);
    }
    if (before.recipientCount !== after.recipientCount) {
        fieldsEdited.push("Recipients");
        properties["Recipient Count Before"] = before.recipientCount;
        properties["Recipient Count After"] = after.recipientCount;
    }

    if (fieldsEdited.length === 0) {
        return undefined;
    }

    const createdAtTime = createdAt ? Date.parse(createdAt) : Number.NaN;

    return {
        ...RECOGNITION_AREA_PROPERTIES,
        "Fields Edited": fieldsEdited,
        "Is Public": after.isPublic,
        ...properties,
        ...(Number.isNaN(createdAtTime) ? {} : { "Card Age Hours": Math.max(0, Math.round((now - createdAtTime) / MILLISECONDS_PER_HOUR)) })
    };
}

export function buildGoodVibesCollectionFilteredProperties(state: GoodVibesFiltersState, location: string): Record<string, unknown> {
    return {
        ...RECOGNITION_AREA_PROPERTIES,
        Location: location,
        ...(state.audience ? { Audience: state.audience } : {}),
        ...(state.direction ? { Direction: state.direction } : {}),
        ...(state.timeframe ? { "Time Frame": state.timeframe } : {}),
        ...(state.collections?.length ? { Collections: [...state.collections] } : {})
    };
}

export function hasGoodVibesFiltersDeviated(state: GoodVibesFiltersState, defaults: GoodVibesFiltersState): boolean {
    return (state.audience !== undefined && state.audience !== defaults.audience) || (state.direction !== undefined && state.direction !== defaults.direction) || (state.timeframe !== undefined && state.timeframe !== defaults.timeframe) || (state.collections?.length ?? 0) > 0;
}
